import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Button,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import UserInfoModal from "../../components/Common/UserInfoModal";
import BroadcastDetailModal from "./helper/broadcastDetailModal";
import { getPendingBroadcastsList } from "./helper/pendingBroadcastsApi";

const PendingBroadcastsPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userInfoModal, setUserInfoModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [broadcastDetailModal, setBroadcastDetailModal] = useState(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);

  // Refs for optimization
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);
  const hasInitialFetchedRef = useRef(false);

  const columns = useMemo(
    () => [
      {
        name: <span className="font-weight-bold fs-13">Broadcast ID</span>,
        selector: (row) => row.broadcast_id,
        sortable: true,
        cell: (row) => (
          <div>
            <div
              className="text-primary cursor-pointer"
              onClick={() => handleBroadcastIdClick(row)}
              style={{ cursor: "pointer" }}
            >
              {row.broadcast_id}
            </div>
          </div>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Title</span>,
        selector: (row) => row.title,
        sortable: true,
        wrap: true,
        cell: (row) => (
          <div style={{ maxWidth: "300px" }}>
            {row.title || "N/A"}
          </div>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Status</span>,
        selector: (row) => row.status,
        sortable: true,
        cell: (row) => (
          <Badge color="warning" className="px-2 py-1">Pending</Badge>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Moderation Decision</span>,
        selector: (row) => row.moderation_decision,
        sortable: true,
        cell: (row) => (
          <Badge color="warning" className="px-2 py-1">
            {row.moderation_decision || "N/A"}
          </Badge>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Moderation Reasons</span>,
        selector: (row) => row.moderation_reasons,
        cell: (row) => {
          if (!row.moderation_reasons || row.moderation_reasons.length === 0) {
            return "-";
          }
          return (
            <div className="d-flex flex-wrap gap-1">
              {row.moderation_reasons.slice(0, 2).map((reason, idx) => (
                <Badge key={idx} color="danger" className="px-2 py-1">
                  {reason}
                </Badge>
              ))}
              {row.moderation_reasons.length > 2 && (
                <Badge color="secondary" className="px-2 py-1">
                  +{row.moderation_reasons.length - 2} more
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        name: <span className="font-weight-bold fs-13">User ID</span>,
        selector: (row) => row.broadcaster?.userId,
        sortable: true,
        cell: (row) => {
          if (!row.broadcaster?.userId) return "-";
          return (
            <div>
              <div
                className="text-primary cursor-pointer"
                onClick={() => handleUserIdClick(row.broadcaster.userId)}
                style={{ cursor: "pointer" }}
              >
                {row.broadcaster.userId}
              </div>
            </div>
          );
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Username</span>,
        selector: (row) => row.broadcaster?.username,
        sortable: true,
        cell: (row) => row.broadcaster?.username || "-",
      },
      {
        name: <span className="font-weight-bold fs-13">Created At</span>,
        selector: (row) => row.createdAt,
        sortable: true,
        cell: (row) => {
          if (!row.createdAt) return "-";
          const date = new Date(row.createdAt);
          return date.toLocaleString();
        },
      },
    ],
    []
  );

  const handleUserIdClick = useCallback((userId) => {
    setSelectedUserId(userId);
    setUserInfoModal(true);
  }, []);

  const handleBroadcastIdClick = useCallback((broadcast) => {
    setSelectedBroadcast(broadcast);
    setBroadcastDetailModal(true);
  }, []);

  const fetchList = useCallback(async () => {
    if (isLoadingRef.current) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    isLoadingRef.current = true;
    setLoading(true);

    try {
      const result = await getPendingBroadcastsList();

      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      setData(result.data);
    } catch (error) {
      if (error.name !== "AbortError" && isMountedRef.current) {
        console.error("Error fetching pending broadcasts list:", error);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
        isLoadingRef.current = false;
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    isMountedRef.current = true;
    if (!hasInitialFetchedRef.current) {
      fetchList();
      hasInitialFetchedRef.current = true;
    }

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Pending Broadcasts" breadcrumbItem="Pending Broadcasts" />
          <Row>
            <Col xs={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    <i className="mdi mdi-broadcast me-2"></i>Pending Broadcasts
                  </h4>
                </CardHeader>
                <CardBody>
                  {/* Data Table */}
                  <DataTable
                    columns={columns}
                    data={data}
                    progressPending={loading}
                    pagination={false}
                    noDataComponent={
                      <div className="text-center py-4">
                        <i className="mdi mdi-broadcast-off" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
                        <p className="mt-3 text-muted">No pending broadcasts found</p>
                      </div>
                    }
                    highlightOnHover
                    pointerOnHover
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <UserInfoModal
        isOpen={userInfoModal}
        toggle={() => {
          setUserInfoModal(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
        onUpdate={() => {
          // Refresh the list after user action
          fetchList();
        }}
      />

      <BroadcastDetailModal
        isOpen={broadcastDetailModal}
        toggle={() => {
          setBroadcastDetailModal(false);
          setSelectedBroadcast(null);
        }}
        broadcast={selectedBroadcast}
        onUpdate={() => {
          // Refresh the list after broadcast action
          fetchList();
        }}
      />
    </React.Fragment>
  );
};

export default PendingBroadcastsPage;

